import { createApi } from '@reduxjs/toolkit/query/react';

import { USER_SUPPORT_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { TopicFact } from 'services/userSupportHelpers';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface ConversationTopic {
    name: string;
    value: string;
    shortTitle: string;
}

export const userSupportApi = createApi({
    baseQuery: getBaseQuery(USER_SUPPORT_API_URL, true),
    reducerPath: 'userSupportApi',
    endpoints: (build) => ({
        getConversationTopics: build.query<ConversationTopic[], { anyPerformance?: boolean }>({
            query: () => '/conversations/topics',
            /**
             * Maps conversation topics from the API to conversation topic objects.
             * If anyPerformance is true, the Performance Issue topic will be included.
             */
            transformResponse: (
                response: { topic_id: string; title: string; short_title: string }[],
                _meta,
                { anyPerformance },
            ) =>
                response
                    ?.map((topic) => ({
                        name: topic.title,
                        value: topic.topic_id,
                        shortTitle: topic.short_title,
                    }))
                    .filter(
                        (topic) => !!anyPerformance || topic.shortTitle !== 'Performance Issue',
                    ),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving conversation topics.',
            ),
        }),
        getTopicFacts: build.query<Record<string, TopicFact>, { topicId: string }>({
            query: ({ topicId }) => `/conversations/topics/${topicId}/facts`,
            /** Maps and reduces conversation topic facts from the API to conversation topic fact dictionary. */
            transformResponse: (response: TopicFact[]) =>
                response?.reduce<Record<string, TopicFact>>((prev, curr) => {
                    let keyName = curr.key_name;
                    if (prev[keyName]) {
                        keyName = `${keyName}__${curr.order_index}`;
                    }
                    return { ...prev, [keyName]: curr };
                }, {}),
        }),
    }),
});

export const { useGetConversationTopicsQuery, useLazyGetTopicFactsQuery } = userSupportApi;
